import type { useAnalyticsEvents } from '@atlaskit/analytics-next';

/**
 * Analytics action-subject for the "personal email entered" signal on the
 * signup form. This is intentionally separate from the
 * `recommendWorkEmailNudge` events: it fires for *every* user who enters a
 * personal-domain email on the signup surface, regardless of whether they are
 * enrolled in the `spark_recommend_work_email_signup` experiment (or in its
 * control vs treatment arm).
 *
 * It provides the experiment-independent denominator — the total population of
 * personal-email signups — against which enrolled/exposed counts can be sized
 * and exposure balance sanity-checked.
 */
export const PERSONAL_EMAIL_ENTERED_SUBJECT = 'signupPersonalEmail';

type CreateAnalyticsEvent = ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'];

/**
 * Fires the `signupPersonalEmail entered` track event.
 *
 * Emitted once per mount, the first time the user has typed a complete
 * personal-domain email (full domain incl. TLD, e.g. "ada@gmail.com"). Fires
 * independently of experiment enrollment so it can be used as the total
 * personal-email population baseline.
 */
export const firePersonalEmailEnteredAnalytics = ({
	createAnalyticsEvent,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
}) => {
	createAnalyticsEvent({
		eventType: 'track',
		actionSubject: PERSONAL_EMAIL_ENTERED_SUBJECT,
		action: 'entered',
		attributes: {
			domainType: 'personal-domain',
		},
	}).fire();
};
